import { memo, useEffect, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { Resizable } from 're-resizable';
import { segment, trackClick, utils } from '@hotjar/tracking';
import { tokens } from '@hotjar/hotsauce';
import { stripDomain } from '@hotjar/webapp/utils/urls';
import type { Site } from '@hotjar/site';
import type { QueryFormatFilter } from '@hotjar/global-filters';
import { useSelector } from '@hotjar/recordings/src/store/player/context';
import {
  useSliderVisibility,
  DEFAULT_TIMEOUT_MS,
} from '@hotjar/recordings/src/features/RecordingsPage/hooks/useSliderVisibility';
import { useTargetingSettings } from '@hotjar/webapp/hooks/useTargetingSettings';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';
import { ErrorConsoleEmptyStateUpsell } from '@hotjar/upsell';
import { ConsoleHeader } from './ConsoleHeader';
import { EmptyState } from './EmptyState';
import { useConsoleEventList } from './hooks/useConsoleEventList';
import { ErrorLog } from './logComponents/ErrorLog';
import { NavigatedToLog } from './logComponents/NavigatedToLog';
import { ConsoleEvent, EVENT_TYPE } from './types';
import { generateNavigationLinkToTrends } from './utils/generateNavigationLinkToTrends';
import { useIsConsoleAvailable } from '../../hooks/useIsConsoleAvailable';

interface ErrorLogProps {
  onTrendNavigationClick: (errrorText: string) => void;
  onCopyActionClick: () => void;
}

const Container = styled.div`
  min-height: 300px;
  height: 100%;
  width: 100%;

  border-top: 1px solid ${tokens.color.neutral_300};
  background: ${tokens.color.bg_info};

  display: flex;
  flex-direction: column;

  z-index: 29;
  user-select: text;
`;

const LogList = styled.ul`
  overflow-y: auto;
`;

const trackActionMenuClick = (name: string, site: Site) => {
  return trackClick({
    name,
    uiPosition: 'console_actions',
    data: {
      state_event_was_triggered_on: 'playback.player_console',
    },
    groupData: utils.extractSiteGroupingProps(site),
  });
};

const renderLog = (
  e: ConsoleEvent,
  index: number,
  errorLogProps: ErrorLogProps,
): JSX.Element | null => {
  if (e.type === EVENT_TYPE.ERROR) {
    return <ErrorLog key={index} event={e.event} {...errorLogProps} />;
  }

  if (e.type === EVENT_TYPE.NAVIGATION) {
    return <NavigatedToLog key={index} event={e.event} />;
  }

  return null;
};

const InternalConsole = ({
  onEnableCapture,
}: {
  onEnableCapture: () => void;
}): JSX.Element | null => {
  const [height, setHeight] = useState(300);

  const [hasEnabledCapture, setHasEnabledCapture] = useState(false);
  const { isConsoleOpen, scrollToTime, recording, site, user, jsonFilters } = useSelector(
    ({ isConsoleOpen, scrollToTime, recording, site, user, jsonFilters }) => ({
      isConsoleOpen,
      scrollToTime,
      recording,
      site,
      user,
      jsonFilters,
    }),
  );

  const isConsoleAvailable = useIsConsoleAvailable();

  const { sessionCaptureConsoleConsent } = useTargetingSettings(site as Site);
  const events = useConsoleEventList(recording);
  const onTrendNavigationClick = useCallback(
    (errorText: string) => {
      trackActionMenuClick('view_trends', site as Site).finally(() => {
        const navigateToTrendsByError = generateNavigationLinkToTrends(
          site?.id as number,
          jsonFilters as QueryFormatFilter,
        );
        window.open(navigateToTrendsByError(errorText), '_blank');
      });
    },
    [site, jsonFilters],
  );
  const onCopyActionClick = useCallback(() => {
    trackActionMenuClick('copy_error', site as Site);
  }, [site]);

  useEffect(() => {
    if (!isConsoleOpen || !events.length || scrollToTime === null) return;

    const log = document.querySelector(`[data-time-in-timeline="${scrollToTime}"]`);
    if (!log) return;

    log.scrollIntoView();
  }, [isConsoleOpen, events, scrollToTime]);

  /**
   * @TODO Figure out a more elegant way to track these events outside components
   */
  useEffect(() => {
    // Recording and user will always be defined by the time we get here
    if (!isConsoleOpen || !recording || !user) return;

    segment.track('Console Opened', {
      site_id: recording.site_id,
      organization_id: recording.organization_id,
      account_id: recording.account_id,
      user_id: user.user_id,
      url_event_was_triggered_on: stripDomain(location.href),
      state_event_was_triggered_on: sessionCaptureConsoleConsent
        ? 'console_tracking_enabled'
        : 'console_tracking_disabled',
      // TODO: Move to store - RECS-1495
      number_errors: events.reduce((acc, e) => (e.type === EVENT_TYPE.ERROR ? acc + 1 : acc), 0),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConsoleOpen]);

  const { visible, open, animating } = useSliderVisibility(isConsoleOpen);
  if (!visible) {
    return null;
  }

  return (
    <Resizable
      size={{ height: open ? height : 0, width: '100%' }}
      style={{ transition: animating ? `height ease-in-out ${DEFAULT_TIMEOUT_MS}ms` : '' }}
      enable={{
        top: true,
        right: false,
        bottom: false,
        left: false,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: false,
      }}
      minHeight="300"
      maxHeight="75vh"
      onResizeStop={(e, direction, ref, delta) => {
        setHeight(height + delta.height);
      }}
    >
      <Container>
        <ConsoleHeader />
        {!isConsoleAvailable ? (
          <ErrorConsoleEmptyStateUpsell
            onUpgradeClick={() => {
              if (site) {
                upsell({
                  site: site as Site,
                  referrer: 'recordings.error_console.empty',
                });
              }
            }}
          />
        ) : (
          <>
            {events.length > 0 ? (
              <LogList>
                {events.map((event, index) =>
                  renderLog(event, index, { onTrendNavigationClick, onCopyActionClick }),
                )}
              </LogList>
            ) : (
              <EmptyState
                isCapturingErrors={sessionCaptureConsoleConsent}
                siteId={site?.id}
                hasEnabledCapture={hasEnabledCapture}
                onEnableCapture={() => {
                  onEnableCapture();
                  setHasEnabledCapture(true);
                }}
              />
            )}
          </>
        )}
      </Container>
    </Resizable>
  );
};

export const Console = memo(InternalConsole);
